import { PREVIEW_MODE } from '$lib/global/constants';

export interface StoreUserAnswerParams {
    campaignVersionId: string;
    questionId?: string;
    answerId: string;
}

export interface StoreUserAnswersParams {
    campaignVersionId: string;
    answers: Record<string, string>;
}

const USER_ANSWERS_KEY_PREFIX = 'userAnswers';

const getUserAnswersKey = (campaignVersionId: string): string => {
    const versionString = PREVIEW_MODE ? 'preview' : campaignVersionId;

    return `${USER_ANSWERS_KEY_PREFIX}_${versionString}`;
};

export const getAllUserAnswers = (campaignVersionId: string): Record<string, string> => {
    if (typeof window === 'undefined') return {};

    try {
        const key = getUserAnswersKey(campaignVersionId);
        const stored = sessionStorage.getItem(key);
        return stored ? JSON.parse(stored) : {};
    } catch (err) {
        console.warn('Failed to get user answers from sessionStorage:', err);

        return {};
    }
};

const setUserAnswers = (campaignVersionId: string, answers: Record<string, string>): void => {
    if (typeof window === 'undefined') return;

    try {
        const key = getUserAnswersKey(campaignVersionId);
        sessionStorage.setItem(key, JSON.stringify(answers));
    } catch (err) {
        console.warn('Failed to set user answers in sessionStorage:', err);
    }
};

export const storeUserAnswer = ({
    campaignVersionId,
    questionId,
    answerId,
}: StoreUserAnswerParams): void => {
    const currentAnswers = { ...getAllUserAnswers(campaignVersionId) };

    // Store using question ID (for advanced linking conditions)
    if (questionId) {
        currentAnswers[questionId] = answerId;
    }

    setUserAnswers(campaignVersionId, currentAnswers);
};

export const storeUserAnswers = ({ campaignVersionId, answers }: StoreUserAnswersParams): void => {
    // Combine current answers with previous answers
    const allAnswers = { ...getAllUserAnswers(campaignVersionId), ...answers };
    setUserAnswers(campaignVersionId, allAnswers);
};

export const getUserAnswer = (
    campaignVersionId: string,
    questionId: string,
): string | undefined => {
    const answers = getAllUserAnswers(campaignVersionId);
    return answers[questionId];
};

export const clearUserAnswers = (campaignVersionId: string): void => {
    if (typeof window === 'undefined') return;

    try {
        const key = getUserAnswersKey(campaignVersionId);
        sessionStorage.removeItem(key);
    } catch (err) {
        console.warn('Failed to clear user answers from sessionStorage:', err);
    }
};
